import React, { useContext, useEffect, useState, useRef } from "react";
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './redux/store';
import { useNavigate, useRoutes, useSearchParams } from "react-router-dom";
import Intro from "./containers/Intro/Intro";
import SelectDocument from "./containers/SelectDocuments/SelectDocument";
import SelectCountry from "./containers/SelectDocuments/SelectCountry";
import CameraAccess from "./containers/CameraAccess";
import HelpFaq from "./containers/HelpFaq";
import FrontDocumentCapture from "./containers/DocumentCapture/FrontCapture";
import BackDocumentCapture from "./containers/DocumentCapture/BackCapture";
import Liveness from "./containers/FaceCapture/Liveness";
import ActiveLiveness from "./containers/FaceCapture/ActiveLiveness";
import Analyzing from "./containers/Analyzing/Analyzing";
import ErrorHandler from "./containers/ErrorHandler/ErrorHandler";
import ImageCheck from "./containers/DocumentCapture/ImageCheck";
import JourneyConfig from "./context/JourneyConfig";
import VerificationComplete from "./containers/VerificationComplete";
import VerificationTerminated from "./containers/VerificationTerminated";
import UserDetails from "./containers/UserDetails";
import SupportingDocument from "./containers/SupportingDocument";
import IntroLiveness from "./containers/IntroLiveness";
import ContinueOnPhone from "./containers/ContinueOnPhone";
import GlobalConfig from "./context/GlobalConfig";
import { useLocation } from "react-router-dom";
import SsnReview from "./containers/SSNReview";
import useDeviceDetection from "./hooks/useDeviceDetection";
import Loader from "./components/Loader/Loader";
import ErrorBoundary from "./components/ErrorBoundary";
import Expired from "./components/Expired";
import UnableToProcess from "./containers/UnableToProcess";
import MobileFrontCapture from "./containers/DocumentCapture/MobileFrontCapture";
import MobileBackCapture from "./containers/DocumentCapture/MobileBackCapture";
import DocumentCapture from "./containers/DocumentCapture/DocumentCapture";
import MobileDocumentCapture from "./containers/DocumentCapture/MobileDocumentCapture";
import { findNextStep } from "./utils/orchestrationModule";
import { trackAction, setHyperDXContext } from "./utils/hyperdx";

// Derive a readable step name from a route path (e.g. "/abc/back/check" -> "back/check").
const routeName = (routePath) => {
  if (!routePath || typeof routePath !== "string") return undefined;
  return routePath.split("?")[0].split("/").filter(Boolean).slice(1).join("/") || routePath;
};

function Base({
  journeyId,
  routesVersion,
  sessionExpired,
  setSessionExpired,
  mobileSessionDone,
  setMobileSessionDone,
  setErrorType,
  journeyExpired,
  currentJourneyState,
  journeyInitialized,
  flowRegion,
  setJourneyInitialized,
  isFirstDocumentVerificationDone,
  setIsFirstDocumentVerificationDone,
  unableToProcessStepNumber,
  setUnableToProcessStepNumber,
  firstVerifiedDocumentName,
  setFirstVerifiedDocumentName
}) {
  const navigate = useNavigate();
  const didInitialRender = useRef(false);
  const isFreshTab = useRef(sessionStorage.getItem("currentStep") === null);
  const [capturingMode, setCapturingMode] = useState("AUTO");
  const [autoCaptureLoadingTime, setAutoCaptureLoadingTime] = useState(null);
  const { journey, setJourney } = useContext(JourneyConfig);
  const { config, routesAndNavigation } = useContext(GlobalConfig);
  const [stepHistory, setStepHistory] = useState([0]);
  const isMobile = useDeviceDetection();
  const sessionId = sessionStorage.getItem("sessionId");
  const [stepObj, setStepObj] = useState({});
  const [navigation, setNavigation] = useState({});
  const [docIteration, setDocIteration] = useState(0);
  const [searchParams] = useSearchParams();
  // When the journey is opened with a ?language= query param (e.g. handed off
  // from the continue-on-phone flow), make it the final selected language.
  // Done synchronously during render (not in an effect) so it is set BEFORE the
  // step components below read sessionStorage on their first render.
  const languageFromQuery = searchParams.get("language");
  if (languageFromQuery && sessionStorage.getItem("language") !== languageFromQuery) {
    sessionStorage.setItem("language", languageFromQuery);
  }
  const urlStep = parseInt(searchParams.get("currentStep")); // Extract currentStep from URL
  const [currentStep, setCurrentStep] = useState(() => {
    const sessionStep = sessionStorage.getItem("currentStep");
    const queryParams = new URLSearchParams(window.location.search);
    const source = queryParams.get("source");
    const FailureReason = queryParams.get("reason");

    if (source === "Digilocker") {
      if (FailureReason) 
      {
        sessionStorage.setItem('documentFailureReason', FailureReason);
      }
      // Routes may not be loaded yet on this fresh return page load. Only compute here if they
      // are; otherwise return 0 and let the stepObj-ready effect navigate to the analysis step
      // once routes load (guards against findNextStep hitting Object.keys(undefined)).
      if(sessionStep!=null && routesAndNavigation?.routes)
      {
          return findNextStep(parseInt(sessionStep), 'analysis', routesAndNavigation.routes);
      }
      return 0;
    }

    if (journeyInitialized == 1) {
      setJourneyInitialized(-1);
      sessionStorage.removeItem("currentStep");
      return 0;
    }
    return sessionStep !== null
      ? parseInt(sessionStep) // If it exists, parse and return it
      : currentJourneyState?.currentStep || // Otherwise, check currentJourneyState
      urlStep || // Then check URL step
      0; // Default to 0
  });

  const [currentDocumentStep, setCurrentDocumentStep] = useState(() => {
    // Priority order: 1. URL, 2. sessionStorage, 3. currentJourneyState
    const fromUrl = searchParams.get("currentDocumentStep");
    const urlVal = fromUrl ? parseInt(fromUrl) : null;
    const stateVal =
      currentJourneyState?.currentDocumentStep !== undefined &&
        currentJourneyState?.currentDocumentStep !== null
        ? parseInt(currentJourneyState?.currentDocumentStep)
        : null;
    const fromSession = sessionStorage.getItem("currentDocumentStep");
    const sessionVal = fromSession !== null ? parseInt(fromSession) : null;
    
    // Check in priority order: URL -> currentJourneyState -> sessionStorage
    if (urlVal !== null && !isNaN(urlVal)) {
      return urlVal;
    }
    
    if (
      (sessionVal !== null && !isNaN(sessionVal)) &&
      (stateVal !== null && !isNaN(stateVal))
    ) {
      return Math.max(sessionVal, stateVal);
    }
    
    if (sessionVal !== null && !isNaN(sessionVal)) {
      return sessionVal;
    }
    
    if (stateVal !== null && !isNaN(stateVal)) {
      return stateVal;
    }
    
    return 0;
  });

  const storedDocumentRetries = () => {
    const stored = parseInt(sessionStorage.getItem("currentDocumentRetries"), 10);
    return Number.isNaN(stored) ? null : stored;
  };
  const [currentDocumentRetries, setCurrentDocumentRetries] = useState(() =>
    searchParams.get("currentDocumentRetries")
      ? parseInt(searchParams.get("currentDocumentRetries")) || 0
      : storedDocumentRetries() ?? (currentJourneyState?.currentDocumentRetries || 0)
  );
  const isFirstJourneyStateSync = useRef(true);
  const urlParams = isMobile ? `?mobile=true&sessionId=${sessionId}` : "";
  const frontImageRef = useRef(null);
  const backImageRef = useRef(null);
  const restrictedPages = ['enable-mobile-flow', 'error', 'expired', 'faq'];
  const location = useLocation();

  // When the journey is opened with a ?language= query param (e.g. handed off
  // from the continue-on-phone flow), make it the final selected language by
  // overriding sessionStorage at journey start.
  useEffect(() => {
    const languageFromQuery = searchParams.get("language");
    if (languageFromQuery) {
      sessionStorage.setItem("language", languageFromQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log("capture base jsx changed:", capturingMode);
}, [capturingMode]);

  useEffect(()=>{
    // The Digilocker URL is delivered once, as a query param on the journey link, and is needed
    // whenever the Digilocker document's turn comes — which for a prefill-verified first document
    // is only AFTER document 1 (launched by the verification-done effect for doc 2, or by
    // Analyzing.jsx for doc 3, both reading it from sessionStorage). Stash it up front, BEFORE the
    // prefill early-return below: otherwise a prefill journey whose Digilocker is document 2 or 3
    // never stashes it (the return skips the stash further down), and document 3's Digilocker never
    // launches — the journey hangs on "Analyzing…".
    const digilockerUrlFromLink = new URLSearchParams(window.location.search).get("digilockerUrl");
    if (digilockerUrlFromLink) {
      sessionStorage.setItem("digilockerUrl", digilockerUrlFromLink);
    }

    // A prefill-verified first document (document-number prefill, e.g. background PAN) is
    // handled by the verification-done effect below, which jumps straight to the second
    // document / launches Digilocker. Skip this document-type-prefill stepping so it does
    // not briefly navigate into a capture screen before that redirect fires.
    if(isFirstDocumentVerificationDone){
      return;
    }
    if(journey?.userInfo?.id?.idType && journey?.userInfo?.id?.country && currentStep === 0 && flowRegion != "Face Verification"){
      const idType = journey.userInfo.id.idType;
      const idType2 = journey?.userInfo?.id?.idType2;
      const idType3 = journey?.userInfo?.id?.idType3;
      // Digilocker arrives under either UI label; the same check was written out three times.
      const isDigilocker = (type) => type === "Digilocker" || type === "Digilocker 2.0";
      if(isDigilocker(idType))
      {
          const queryParams = new URLSearchParams(window.location.search);
          const digilockerUrl = queryParams.get("digilockerUrl");
          if(digilockerUrl) {
              sessionStorage.setItem("digilockerUrl", digilockerUrl);
              window.location.replace(digilockerUrl);
          }
          else{
            console.log("Digilocker url is missing");
          }
      }
      // A prefilled Digilocker on document 2 OR 3 behaves the same way: the applicant captures
      // document 1 first, so the Digilocker URL is stashed for when that document's turn comes.
      // Document 3 used to fall through to the plain camera branch, stranding the prefill.
      else if(isDigilocker(idType2) || isDigilocker(idType3))
      {
          const queryParams = new URLSearchParams(window.location.search);
          const digilockerUrl = queryParams.get("digilockerUrl");
          if(digilockerUrl) {
              sessionStorage.setItem("digilockerUrl", digilockerUrl);
          }
          sessionStorage.removeItem("document");
          sessionStorage.setItem('document', journey?.userInfo?.id?.idType);
          stepperFunc(currentJourneyState?.currentStep + 3)
      }
      else
      {
          const cameraAccessAllowed = sessionStorage.getItem('cameraAccessAllowed');
          sessionStorage.removeItem("document");
          sessionStorage.setItem('document', journey?.userInfo?.id?.idType);
          stepperFunc(currentJourneyState?.currentStep + cameraAccessAllowed ? 4 : 3)
      }
    }
  }, [])

  useEffect(() => {
    const unableToProcessStep = Number(unableToProcessStepNumber);
    // When returning from Digilocker (?source=Digilocker) the currentStep initializer already
    // routes to the second document's analysis. This effect must NOT run then — otherwise, if
    // the fresh page load resolves currentStep to 0 before routes finish loading, it would
    // re-launch Digilocker (the URL is still in sessionStorage) and loop indefinitely.
    const isReturningFromDigilocker =
      new URLSearchParams(window.location.search).get("source") === "Digilocker";
    // Only a PREFILL-verified first document may jump here. Analyzing.jsx also flips
    // isFirstDocumentVerificationDone once doc-1 is verified in the normal flow, and it has
    // already navigated to the second document itself — jumping again would land a step past
    // it (on doc-2's camera). Prefill is the case where doc-1's screens were never visited,
    // so currentStep is still at the journey's start.
    const isPrefillVerifiedFirstDocument = currentStep === 0;
    if (!isReturningFromDigilocker && isPrefillVerifiedFirstDocument && isFirstDocumentVerificationDone && unableToProcessStepNumber != -1 && !isNaN(currentStep) && currentStep <= unableToProcessStep) {
      // The first document was verified via prefill (e.g. background PAN), so doc-1's
      // screens are skipped. Mirror Analyzing.jsx's post-doc1 handling for the second
      // document: when it's Digilocker, launch it directly instead of landing on the
      // second-document selection picker.
      const idType2 = journey?.userInfo?.id?.idType2;
      if (idType2 === "Digilocker" || idType2 === "Digilocker 2.0") {
        const queryParams = new URLSearchParams(window.location.search);
        const digilockerUrl = queryParams.get("digilockerUrl") || sessionStorage.getItem("digilockerUrl");
        if (digilockerUrl) {
          sessionStorage.setItem("digilockerUrl", digilockerUrl);
          sessionStorage.removeItem("document");
          sessionStorage.setItem("document", idType2);
          // On Digilocker return (?source=Digilocker) Base resolves the resume step via
          // findNextStep(sessionStorage.currentStep, 'analysis'). In the normal flow Digilocker
          // is launched from the second document's selection screen, so currentStep already points
          // there. This prefill redirect skips that screen, so set currentStep to the second
          // document's selection index (unableToProcessStep + 1) first — otherwise the return lands
          // on the FIRST (doc-1) analysis step instead of the second document's.
          sessionStorage.setItem("currentStep", String(unableToProcessStep + 1));
          window.location.replace(digilockerUrl);
          return;
        }
        console.log("Digilocker url is missing for prefilled second document");
      }
      stepperFunc(unableToProcessStep + 1);
    }
  }, [isFirstDocumentVerificationDone, unableToProcessStepNumber])

  const navigateToWithoutReview = {
    0: `/${journeyId}${urlParams}`,
    1: `/${journeyId}/country${urlParams}`,
    2: `/${journeyId}/documents${urlParams}`,
    3: `/${journeyId}/camera${urlParams}`,
    4: `/${journeyId}/front${urlParams}`,
    5: `/${journeyId}/front/check${urlParams}`,
    6: `/${journeyId}/back${urlParams}`,
    7: `/${journeyId}/back/check${urlParams}`,
    8: `/${journeyId}/analysis${urlParams}`,
    9: `/${journeyId}/unable-to-process${urlParams}`,
    // 9: `/${journeyId}/liveness-intro${urlParams}`,
    11: `/${journeyId}/liveness${urlParams}`,
    12: `/${journeyId}/complete${urlParams}`,
    13: `/${journeyId}/enable-mobile-flow`,
    14: `/${journeyId}/faq${urlParams}`,
    15: `/${journeyId}/expired`,
    16: `/${journeyId}/error`,
  };
  const defaultNavigation = {
    0: [1, 14, null],
    1: [2, 14, 0],
    2: [3, 14, 1],
    3: [4, 14, 2],
    4: [5, 14, 2],
    5: [6, 14, 4],
    6: [7, 14, 5],
    7: [8, 14, 6],
    8: [11, 9, null],
    9: [3, 2, 7],
    11: [12, 14, null],
    12: [null, null, null],
    13: [null, null, null],
    14: [null, null, null],
    15: [null, null, null],
    16: [null, null, null],
  };

  useEffect(() => {
    if (
      !isNaN(currentStep) &&
      Object.keys(stepObj).length &&
      stepObj?.[currentStep] !== location.pathname
    ) {
      const reversed = Object.keys(stepObj)
        .map(Number)
        .filter((key) => key < Number(currentStep))
        .reverse();
      const goTo = reversed.find((key) => stepObj?.[key] == location.pathname);

      if (!isNaN(goTo) && goTo >= 0) {
        setCurrentStep(parseInt(goTo));
      }
    }
  }, [location]);

  useEffect(() => {
    if (mobileSessionDone) {
      setCurrentStep(
        currentJourneyState?.currentStep ||
        parseInt(sessionStorage.getItem("currentStep")) ||
        0
      );
      setMobileSessionDone(false);
    }
    if (
      currentJourneyState?.currentDocumentStep !== undefined &&
      currentJourneyState?.currentDocumentStep !== null &&
      !isNaN(parseInt(currentJourneyState?.currentDocumentStep))
    ) {
      const backendDocStep = parseInt(currentJourneyState?.currentDocumentStep);
      const storedDocStepRaw = sessionStorage.getItem("currentDocumentStep");
      const storedDocStep =
        storedDocStepRaw !== null && !isNaN(parseInt(storedDocStepRaw))
          ? parseInt(storedDocStepRaw)
          : null;
      const nextDocStep =
        storedDocStep !== null ? Math.max(backendDocStep, storedDocStep) : backendDocStep;
      setCurrentDocumentStep(nextDocStep);
      sessionStorage.setItem("currentDocumentStep", String(nextDocStep));
    }
    const keepStoredRetries = isFirstJourneyStateSync.current && storedDocumentRetries() !== null;
    isFirstJourneyStateSync.current = false;
    if (!keepStoredRetries) {
      setCurrentDocumentRetries(currentJourneyState?.currentDocumentRetries || 0);
    }
  }, [currentJourneyState]);

  useEffect(() => {
    const isError = sessionStorage.getItem("error");
    if (
      !isError &&
      !restrictedPages.some((page) => stepObj?.[currentStep]?.includes(page))
    ) {
      sessionStorage.setItem("currentStep", parseInt(currentStep));
      sessionStorage.removeItem("nonImportantStep");
    }
  }, [currentStep]);

  useEffect(() => {
    if (typeof currentDocumentStep === "number" && !isNaN(currentDocumentStep)) {
      sessionStorage.setItem(
        "currentDocumentStep",
        String(parseInt(currentDocumentStep))
      );
    }
  }, [currentDocumentStep]);

  useEffect(() => {
    sessionStorage.setItem("currentDocumentRetries", String(currentDocumentRetries));
  }, [currentDocumentRetries]);

  const stepperFunc = (step) => {
    const isError = sessionStorage.getItem("error");
    if (
      step != stepHistory[stepHistory.length - 1] &&
      !restrictedPages.some((page) => stepObj?.[step]?.includes(page)) &&
      !isError
    ) {
      setStepHistory([...stepHistory, step]);
    }
    if (isError) {
      const goTo = Object.keys(stepObj)
        .map(Number)
        .reverse()
        .find((key) => stepObj?.[key].includes("error"));
      setCurrentStep(goTo);
      navigate(stepObj?.[goTo] ?? stepObj?.[step] ?? 0, { replace: true });
      trackAction("journey:step", {
        step: goTo,
        route: routeName(stepObj?.[goTo]),
        from: currentStep,
        fromRoute: routeName(stepObj?.[currentStep]),
        viaError: true,
      });
    } else if (!isNaN(step)) {
      const isBack = parseInt(step) < parseInt(currentStep);
      setCurrentStep(parseInt(step));
      navigate(stepObj[step], { replace: true });
      trackAction("journey:step", {
        step: parseInt(step),
        route: routeName(stepObj?.[step]),
        from: currentStep,
        fromRoute: routeName(stepObj?.[currentStep]),
        direction: isBack ? "back" : "forward",
      });
    }
  };

  const resumeStepFor = (step) => {
    if (!isFreshTab.current || !stepObj?.[step]?.includes("/unable-to-process")) return step;
    const selectionStep = navigation?.[step]?.[1];
    return stepObj?.[selectionStep]?.includes("/documents") ? selectionStep : step;
  };

  const populateRoutes = (routes, journeyId, urlParams) => {
    return Object.fromEntries(
      Object.entries(routes).map(([key, value]) => {
        const replacedValue = value
          .replace(/{journeyId}/g, journeyId)
          .replace(/{urlParams}/g, urlParams);
        return [key, replacedValue];
      })
    );
  };
  useEffect(() => {
    if (!didInitialRender.current) {
      didInitialRender.current = true;
      return;
    }
    if (Object.keys(stepObj).length) {
      // On Digilocker return, route to the second document's analysis step now that routes are
      // loaded — the currentStep initializer may have run before routes were ready (returning 0).
      // stepperFunc navigates to a path without the ?source=Digilocker query, so this self-clears.
      const digilockerSource = new URLSearchParams(window.location.search).get("source");
      if (digilockerSource === "Digilocker") {
        // The saved numeric currentStep is unreliable across the Digilocker round-trip (the
        // orchestration is rebuilt on return and indices can shift). Pick the analysis step for
        // the CURRENT document directly: the Nth /analysis route where N = currentDocumentStep
        // (0-indexed: doc-1 = first analysis, doc-2 = second, ...).
        const analysisSteps = Object.entries(stepObj)
          .filter(([, route]) => route.includes("/analysis"))
          .map(([k]) => Number(k))
          .sort((a, b) => a - b);
        const docIdx = Number.isInteger(currentDocumentStep) ? currentDocumentStep : 0;
        const target = analysisSteps[docIdx] ?? analysisSteps[analysisSteps.length - 1] ?? 0;
        // Clear the stored Digilocker URL now that the return is handled. Once we navigate to
        // /analysis the ?source=Digilocker query is gone, so without this the pendingDigilockerRedirect
        // loader gate would flip back on (digilockerUrl still present) and hide the analysis screen.
        sessionStorage.removeItem("digilockerUrl");
        stepperFunc(target);
      } else {
        const resumeStep = resumeStepFor(currentStep);
        isFreshTab.current = false;
        if (resumeStep !== currentStep) sessionStorage.removeItem("documentFailureReason");
        stepperFunc(resumeStep);
      }
    }
  }, [stepObj]);

  // Segmentation attributes for behavioural analysis (device, region, language,
  // selected document). Re-applied as journey context becomes available.
  useEffect(() => {
    setHyperDXContext({
      deviceType: isMobile ? "mobile" : "desktop",
      region: flowRegion,
      language: sessionStorage.getItem("language") ?? "en",
      country:
        journey?.capturedData?.country?.value ??
        journey?.capturedData?.country ??
        journey?.userInfo?.id?.country,
      idType: journey?.capturedData?.idType ?? journey?.userInfo?.id?.idType,
    });
  }, [isMobile, flowRegion, journey]);


  useEffect(() => {
    if (routesAndNavigation?.routes) {
      const populatedRoutes = populateRoutes(
        routesAndNavigation.routes,
        journeyId,
        urlParams
      );
      setStepObj(populatedRoutes);
      setNavigation(routesAndNavigation.navigation);
    } else {
      // Final fallback to hardcoded navigation
      setStepObj(navigateToWithoutReview);
      setNavigation(defaultNavigation);
    }
  }, [routesAndNavigation, journeyId, flowRegion]);

  const FrontCaptureComponent = isMobile
    ? MobileFrontCapture
    : FrontDocumentCapture;
  const BackCaptureComponent = isMobile
    ? MobileBackCapture
    : BackDocumentCapture;

  const DocumentCaptureComponent = isMobile ? MobileDocumentCapture : DocumentCapture;

  const routes = [
    {
      path: "/",
      element: <Loader />,
    },
    {
      path: "/:journeyId",
      element: (
        <Intro
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          currentStep={currentStep}
        />
      ),
    },
    {
      path: "/:journeyId/country",
      element: (
        <SelectCountry
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          currentStep={currentStep}
          currentDocumentStep={currentDocumentStep}
        />
      ),
    },
    {
      path: "/:journeyId/documents",
      element: (
        <SelectDocument
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          firstVerifiedDocumentName={firstVerifiedDocumentName}
          currentJourneyState={currentJourneyState}
          currentDocumentRetries={currentDocumentRetries}
          stepHistory={stepHistory}
          docIteration={docIteration}
          currentDocumentStep={currentDocumentStep}
          stepObj={stepObj}
          currentStep={currentStep}
        />
      ),
    },
    {
      path: '/:journeyId/user-details',
      element: (
        <UserDetails
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          currentStep={currentStep}
          currentDocumentStep={currentDocumentStep}
        />
      ),
    },
    {
      path: '/:journeyId/camera',
      element: <CameraAccess stepperFunc={stepperFunc} isMobile={isMobile} currentDocumentStep={currentDocumentStep} steps={navigation[currentStep]} navigation={navigation} currentStep={currentStep}/>
    },
    {
      path: "/:journeyId/doc-capture",
      element: (
        <DocumentCaptureComponent
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          docIteration={docIteration}
          currentDocumentStep={currentDocumentStep}
          currentStep={currentStep}
          frontImageRef={frontImageRef}
          backImageRef={backImageRef}
          currentDocumentRetries={currentDocumentRetries}
        />
      ),
    },
    {
      path: "/:journeyId/front",
      element: (
        <FrontCaptureComponent
          stepperFunc={stepperFunc}
          isFirstDocumentVerificationDone={isFirstDocumentVerificationDone}
          currentDocumentRetries={currentDocumentRetries}
          capturingMode={capturingMode}
          setCapturingMode={setCapturingMode}
          setAutoCaptureLoadingTime={setAutoCaptureLoadingTime}
          steps={navigation[currentStep]}
          currentStep={currentStep}
          docIteration={docIteration}
          currentDocumentStep={currentDocumentStep}
          frontImageRef={frontImageRef}
        />
      ),
    },
    {
      path: "/:journeyId/front/check",
      element: (
        <ImageCheck
          stepperFunc={stepperFunc}
          triggeredFrom={"front"}
          stepObj={stepObj}
          steps={navigation[currentStep]}
          autoCaptureLoadingTime={autoCaptureLoadingTime}
          capturingMode={capturingMode}
          currentStep={currentStep}
          image={frontImageRef.current}
          isFirstDocumentVerificationDone={isFirstDocumentVerificationDone}
          currentDocumentStep={currentDocumentStep}
          currentDocumentRetries={currentDocumentRetries}
        />
      ),
    },
    {
      path: "/:journeyId/back",
      element: (
        <BackCaptureComponent
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          docIteration={docIteration}
          currentStep={currentStep}
          currentDocumentStep={currentDocumentStep}
          backImageRef={backImageRef}
          setAutoCaptureLoadingTime={setAutoCaptureLoadingTime}
          capturingMode={capturingMode}
          setCapturingMode={setCapturingMode}
        />
      ),
    },
    {
      path: "/:journeyId/back/check",
      element: (
        <ImageCheck
          stepperFunc={stepperFunc}
          triggeredFrom={"back"}
          stepObj={stepObj}
          steps={navigation[currentStep]}
          image={backImageRef.current}
          isFirstDocumentVerificationDone={isFirstDocumentVerificationDone}
          currentStep={currentStep}
          currentDocumentStep={currentDocumentStep}
          currentDocumentRetries={currentDocumentRetries}
          autoCaptureLoadingTime={autoCaptureLoadingTime}
          capturingMode={capturingMode}
        />
      ),
    },
    {
      path: "/:journeyId/analysis",
      element: (
        <Analyzing
          stepperFunc={stepperFunc}
          stepObj={stepObj}
          currentDocumentRetries={currentDocumentRetries}
          setErrorType={setErrorType}
          frontImageRef={frontImageRef}
          backImageRef={backImageRef}
          navigation={navigation}
          steps={navigation[currentStep]}
          currentStep={currentStep}
          currentDocumentStep={currentDocumentStep}
          setFirstVerifiedDocumentName={setFirstVerifiedDocumentName}
          setJourney={setJourney}
          isFirstDocumentVerificationDone={isFirstDocumentVerificationDone}
          updateDocumentStep={(success) => {
            if (success) {
              setCurrentDocumentStep(
                Math.min(
                  currentDocumentStep + 1,
                  config?.documentCapture?.length
                    ? config.documentCapture.length - 1
                    : 0
                )
              );
              setIsFirstDocumentVerificationDone(true);
              setCurrentDocumentRetries(0);
            } else {
              setCurrentDocumentRetries(currentDocumentRetries + 1);
              if (
                config?.documentCapture?.[currentDocumentStep]?.tries &&
                config?.documentCapture?.[currentDocumentStep]?.tries ==
                currentDocumentRetries &&
                config?.documentCapture?.[currentDocumentStep]?.tries != 0
              )
                return false;
              else return true;
            }
          }}
        />
      ),
    },
    {
      path: "/:journeyId/ssn-review",
      element: (
        <SsnReview
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          currentStep={currentStep}
        />
      ),
    },
    {
      // Capture-only supporting document. Sits after every ID document and before liveness,
      // matching the SUPPORTING_DOC step in one-journey-backend's journey state machine.
      path: "/:journeyId/supporting-document",
      element: (
        <SupportingDocument
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          currentStep={currentStep}
        />
      ),
    },
    {
      path: "/:journeyId/liveness-intro",
      element: (
        <Intro
          stepperFunc={stepperFunc}
          docCaptured
          steps={navigation[currentStep]}
          currentStep={currentStep}
        />
      ),
    },
    {
      path: "/:journeyId/liveness/instruction",
      element: (
        <IntroLiveness
          stepperFunc={stepperFunc}
          docCaptured
          steps={navigation[currentStep]}
          currentStep={currentStep}
        />
      ),
    },
    {
      path: "/:journeyId/liveness",
      element: (
        config?.faceCapture?.livenessType === "ACTIVE" ? (
          <ActiveLiveness
            stepperFunc={stepperFunc}
            steps={navigation[currentStep]}
            currentStep={currentStep}
            currentDocumentStep={currentDocumentStep}
            isMobile={isMobile}
          />
        ) : (
          <Liveness
            stepperFunc={stepperFunc}
            steps={navigation[currentStep]}
            currentStep={currentStep}
            currentDocumentStep={currentDocumentStep}
          />
        )
      ),
    },
    {
      path: "/:journeyId/complete",
      element: (
        <VerificationComplete
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          currentStep={currentStep}
        />
      ),
    },
    {
      path: "/:journeyId/faq",
      element: (
        <HelpFaq
          stepperFunc={stepperFunc}
          stepHistory={stepHistory}
          steps={navigation[currentStep]}
          currentStep={currentStep}
        />
      ),
    },
    {
      path: "/:journeyId/enable-mobile-flow",
      element: (
        <ContinueOnPhone
          stepperFunc={stepperFunc}
          stepHistory={stepHistory}
          steps={navigation[currentStep]}
          currentStep={currentStep}
          currentDocumentStep={currentDocumentStep}
          currentDocumentRetries={currentDocumentRetries}
        />
      ),
    },
    {
      path: "/:journeyId/error",
      element: (
        <ErrorHandler
          stepperFunc={stepperFunc}
          isSessionExpired={sessionExpired}
          steps={navigation[currentStep]}
        />
      ),
    },
    {
      path: "/error",
      element: (
        <ErrorHandler
          stepperFunc={stepperFunc}
          isSessionExpired={sessionExpired}
          steps={navigation[currentStep]}
        />
      ),
    },
    {
      path: "/:journeyId/expired",
      element: (
        <Expired
          stepperFunc={stepperFunc}
          isSessionExpired={sessionExpired}
          currentStep={currentStep}
          setSessionExpired={setSessionExpired}
          isJourneyExpired={journeyExpired}
          steps={navigation[currentStep]}
        />
      ),
    },
    {
      path: "*",
      element: (
        <ErrorHandler
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
        />
      ),
    },
    {
      path: "/:journeyId/unable-to-process",
      element: (
        <UnableToProcess
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          currentStep={currentStep}
          currentDocumentStep={currentDocumentStep}
          documentFailureReason={currentJourneyState?.docFailureReason}
        />
      ),
    },
    {
      path: "/:journeyId/verification-terminated",
      element: (
        <VerificationTerminated
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
        />
      ),
    },
  ];

  const routeElements = useRoutes(routes);

  // When the first document was verified via prefill and the second document is Digilocker,
  // the verification-done effect redirects to the Digilocker URL. Render a loader instead of
  // the routed step until that redirect fires so no capture/consent screen flashes.
  const prefilledSecondIdType = journey?.userInfo?.id?.idType2;
  // Not when returning from Digilocker (?source=Digilocker) — the doc is done and we must render
  // the routed analysis step, not keep looping on the loader (digilockerUrl is still in sessionStorage).
  // Not once the document has failed either: the analysis response re-stashes a fresh
  // digilockerUrl for the retry button, which would otherwise flip this gate back on and
  // hide the unable-to-process screen behind the loader. After a failure the applicant
  // presses Retry — nothing auto-redirects, so there is nothing to wait for.
  const pendingDigilockerRedirect =
    !sessionStorage.getItem("documentFailureReason") &&
    new URLSearchParams(window.location.search).get("source") !== "Digilocker" &&
    isFirstDocumentVerificationDone &&
    (prefilledSecondIdType === "Digilocker" || prefilledSecondIdType === "Digilocker 2.0") &&
    !!(new URLSearchParams(window.location.search).get("digilockerUrl") || sessionStorage.getItem("digilockerUrl"));
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ErrorBoundary
          stepperFunc={stepperFunc}
          steps={navigation[currentStep]}
          journeyId={journeyId}
          stepObj={stepObj}
        >
          {/* <div className="upper md:h-auto h-[100%] w-full sm:h-full flex flex-row justify-center md:items-center" style={{ backgroundColor: "#F4F4F4" }}>
            <div
              className=" inner rounded-[16px] md:w-[900px] w-full md:h-[620px] h-[100%] flex flex-col md:pl-[24px] md:pr-[24px] md:pt-[10px] md:pb-[10px] md:gap-[10px]"
              style={{ backgroundColor: "#FFFFFF" }}
            > */}
          <div className="upper" style={{ backgroundColor: "#F4F4F4" }}>
            <div className="inner" style={{ backgroundColor: "#FFFFFF" }}>
              {pendingDigilockerRedirect ? <Loader /> : routeElements}
            </div>
          </div>
        </ErrorBoundary>
      </PersistGate>
    </Provider>
  );
}
export default Base;